import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
/* eslint-disable  import/no-cycle */
import { RootState } from 'store/rootReducer';
import keyBy from 'lodash/keyBy';
import set from 'lodash/set';
import { ApiErrors, Loaders } from 'api/types';
import { UNAUTHORIZED, FORBIDDEN, CONFLICT } from 'http-status-codes';
import { i18n } from 'helpers/i18n';
import { axiosInstance } from '../axiosInstance';

export type Component = {
  created_at: string;
  id: string;
  items: any[];
  link_id: string;
  link_type: string;
  name: string;
  optional: boolean;
  updated_at: string;
};

export type ComponentsState = {
  apiErrors: ApiErrors;
  availableItems: any;
  editComponent?: Component | undefined;
  list: {
    [key: string]: Component;
  };
  loaders: Loaders;
  loadingAvailableItems: boolean;
  loadingComponents: boolean;
};

// TODO: refactor to object
const initialState: ComponentsState = {
  apiErrors: {},
  availableItems: [],
  loaders: {},
  list: {},
  loadingAvailableItems: false,
  loadingComponents: false
};

interface FetchComponentsPayload {
  availableItems: any[];
  components: Component[];
}

interface FetchComponentsParams {
  getAvailableItems: boolean;
  productId: string;
}

export const fetchComponents = createAsyncThunk<FetchComponentsPayload, FetchComponentsParams, { rejectValue: string }>(
  'components/fetchComponents',
  async ({ getAvailableItems, productId }, { rejectWithValue }) => {
    try {
      const componentsResponse = await axiosInstance.get(`/products/${productId}/components`);
      const {
        data: { components }
      } = componentsResponse;
      let itemsMapped = [];
      if (getAvailableItems) {
        const availableItemsResponse = await axiosInstance.get(`/products/${productId}/components/items/available`);
        const {
          data: { items }
        } = availableItemsResponse;
        itemsMapped = items.map((e: any) => ({
          label: e.name,
          value: e.link_id
        }));
      }

      return {
        components,
        availableItems: itemsMapped
      };
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('components.you cannot create this component'));
      }

      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

export const deleteComponent = createAsyncThunk<
  string,
  DeleteComponentParams,
  { rejectValue: { id: string; msg: string } }
>('components/deleteComponent', async ({ productId, componentId }, { dispatch, rejectWithValue }) => {
  try {
    dispatch(updateListDelete({ id: componentId, value: true }));
    await axiosInstance.delete(`/products/${productId}/components/${componentId}`);
    return componentId;
  } catch (err) {
    if (!err.response) {
      return rejectWithValue({ id: componentId, msg: i18n.t('errors.something went wrong') });
    }
    const { status } = err.response;
    if (status === UNAUTHORIZED || status === FORBIDDEN) {
      return rejectWithValue({
        id: componentId,
        msg: i18n.t('components.you cannot delete this component')
      });
    }

    return rejectWithValue({ id: componentId, msg: i18n.t('errors.something went wrong') });
  } finally {
    dispatch(updateListDelete({ id: componentId, value: false }));
  }
});

// TODO: unify with componetformdialog
type ComponentFormProps = {
  id?: string;
  items: string[];
  name: string;
  optional: boolean;
};

interface SaveComponentParams {
  component: ComponentFormProps;
  componentId?: string;
  productId: string;
}

export const saveComponent = createAsyncThunk<Component, SaveComponentParams, { rejectValue: string }>(
  'components/saveComponent',
  async ({ component, componentId, productId }, { rejectWithValue }) => {
    let response;
    try {
      if (componentId) {
        response = await axiosInstance.put(`/products/${productId}/components/${componentId}`, component);
      } else {
        response = await axiosInstance.post(`/products/${productId}/components`, component);
      }
      const { data } = response;
      return data;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('components.you cannot update this component'));
      }
      if (status === CONFLICT) {
        return rejectWithValue(i18n.t('components.conflict'));
      }
      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

interface DeleteComponentParams {
  componentId: string;
  productId: string;
}

const productsSlice = createSlice({
  initialState,
  name: 'components',
  reducers: {
    closeError: state => {
      state.apiErrors = {};
    },
    updateListDelete: (state, { payload }: PayloadAction<{ id: string; value: boolean }>) => {
      const { id, value } = payload;
      if (value) {
        set(state, `loaders.deletingErrorItems.${id}`, '');
      }
      set(state, `loaders.isDeletingItems.${id}`, value);
    },
    setEditComponent: (state, { payload }: PayloadAction<Component | undefined>) => {
      state.editComponent = payload;
      state.apiErrors = {
        savingError: ''
      };
    },
    resetComponentsList: state => initialState
  },
  extraReducers: builder =>
    builder
      .addCase(fetchComponents.fulfilled, (state, { payload }) => {
        state.loaders.isLoading = false;
        const { availableItems, components } = payload;
        state.list = keyBy(components, 'id');
        state.availableItems = availableItems;
      })
      .addCase(fetchComponents.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchComponents.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(deleteComponent.fulfilled, (state, { payload }) => {
        delete state.list[`${payload}`];
      })
      .addCase(deleteComponent.rejected, (state, { payload }) => {
        set(state, `apiErrors.savingErrorItems.${payload?.id}`, payload?.msg || i18n.t('errors.something went wrong'));
      })
      .addCase(saveComponent.fulfilled, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.list[payload.id] = payload;
      })
      .addCase(saveComponent.pending, state => {
        state.loaders.isSaving = true;
        state.apiErrors.savingError = '';
      })
      .addCase(saveComponent.rejected, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.apiErrors.savingError = payload || i18n.t('errors.something went wrong');
      })
});

const { actions, reducer } = productsSlice;
export { reducer as components };
export const { closeError, updateListDelete, setEditComponent, resetComponentsList } = actions;

export const getComponents = (state: RootState) => state.components.list;
export const getEditComponent = (state: RootState) => state.components.editComponent;
export const getAvailableItems = (state: RootState) => state.components.availableItems;
export const getComponentsLoaders = (state: RootState) => state.components.loaders;
export const getComponentsApiErrors = (state: RootState) => state.components.apiErrors;
export const isLoadingAvailableItems = (state: RootState) => state.components.loadingAvailableItems;
